import { createContext, useContext, useMemo } from 'react'

import { DEFAULT_LABELS, type LabelKey } from './defaults'

/**
 * Shape of a host translator. Matches `@susu/i18n`'s `TranslationFunction`, so a
 * host can pass its own `t` straight through without an adapter.
 */
export type Translate = (
  key: string,
  parameters?: Record<string, string>,
) => string

export type LabelFunction = (
  key: LabelKey,
  parameters?: Record<string, string>,
) => string

/**
 * Key of a count-dependent label, given without its category suffix — pass
 * `orderList.list.itemCount`, not `orderList.list.itemCount.one`.
 */
export type PluralLabelKey = PluralBase<LabelKey>

type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other'

// Naked type parameter so the conditional distributes across the LabelKey
// union; checking `LabelKey` inline tests the whole union at once and yields
// `never`.
type PluralBase<K> = K extends `${infer Base}.${PluralCategory}` ? Base : never

export type PluralLabelFunction = (
  key: PluralLabelKey,
  count: number,
  parameters?: Record<string, string>,
) => string

const TranslateContext = createContext<Translate | null>(null)
const LanguageContext = createContext<string>('en')

/** `{name}` placeholders, same syntax as `@susu/i18n`'s `interpolate`. */
function interpolate(
  template: string,
  parameters?: Record<string, string>,
): string {
  if (!parameters) return template
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    parameters[name] !== undefined ? parameters[name] : `{${name}}`,
  )
}

export function OrderListTranslationsProvider({
  t,
  language = 'en',
  children,
}: Readonly<{
  t?: Translate
  /** BCP-47 code, used only to pick a plural category. */
  language?: string
  children: React.ReactNode
}>) {
  return (
    <TranslateContext.Provider value={t ?? null}>
      <LanguageContext.Provider value={language}>
        {children}
      </LanguageContext.Provider>
    </TranslateContext.Provider>
  )
}

/**
 * Resolves this package's user-facing copy.
 *
 * Falls back to the bundled English in two cases: no host translator at all
 * (the standalone dev app), and a host whose dictionary lacks the key. The
 * second one needs the `!== key` check because `@susu/i18n`'s `translate`
 * returns the key itself when it cannot resolve it — so "missing" arrives as a
 * value, not as `undefined`, and without this a German user would see
 * `orderList.summary.subtotal` on screen.
 */
export function useLabel(): LabelFunction {
  const t = useContext(TranslateContext)

  return useMemo(
    () => (key, parameters) => {
      const translated = t?.(key, parameters)
      if (translated !== undefined && translated !== key) return translated
      return interpolate(DEFAULT_LABELS[key], parameters)
    },
    [t],
  )
}

/**
 * Resolves a count-dependent label.
 *
 * `@susu/i18n` has no plural support — `translate` is key to string — so the
 * category is chosen here and appended to the key: `…itemCount` asks for
 * `…itemCount.one` or `…itemCount.other`. `count` is interpolated as `{count}`.
 *
 * `Intl.PluralRules` picks the category for the host's language, so Polish
 * correctly asks for `few`/`many` where English never does. The bundled English
 * only defines `one` and `other`, and a category the dictionary lacks falls back
 * to `other` — so a Polish `few` renders the `other` form until someone adds
 * that key, rather than rendering nothing.
 */
export function usePluralLabel(): PluralLabelFunction {
  const t = useContext(TranslateContext)
  const language = useContext(LanguageContext)

  return useMemo(() => {
    let rules: Intl.PluralRules
    try {
      rules = new Intl.PluralRules(language)
    } catch {
      // An unknown or malformed tag would otherwise throw on every render.
      rules = new Intl.PluralRules('en')
    }

    return (key, count, parameters) => {
      const withCount = { ...parameters, count: String(count) }

      // The selected category first, `other` as the fallback. Resolution has to
      // go through the dictionaries directly rather than `useLabel`, because a
      // category the bundled English does not define (Polish `few`) has no
      // DEFAULT_LABELS entry and `interpolate(undefined)` would throw.
      for (const candidate of [
        `${key}.${rules.select(count)}`,
        `${key}.other`,
      ]) {
        const hosted = t?.(candidate, withCount)
        if (hosted !== undefined && hosted !== candidate) return hosted

        const bundled = DEFAULT_LABELS[candidate as LabelKey]
        if (bundled !== undefined) return interpolate(bundled, withCount)
      }

      return `${key}.other`
    }
  }, [t, language])
}
